import { splitCommandBatch, tokenizeCommandInput } from "./commandTokenization";

export type SheetCommandVerb = "credit" | "debit" | "bbal" | "clear" | "comment" | "edit" | "move" | "rename" | "title" | "delete" | "help";

export type ParseSheetCommandErrorCode =
  | "EMPTY_COMMAND"
  | "INVALID_COMMAND"
  | "MISSING_ACCOUNT"
  | "INVALID_ACCOUNT"
  | "ACCOUNT_TOO_LONG"
  | "MISSING_AMOUNT"
  | "INVALID_AMOUNT"
  | "INVALID_OPTIONAL_TOKEN"
  | "INVALID_LETTER"
  | "MISSING_COMMENT"
  | "INVALID_COMMENT"
  | "INVALID_RENAME_SYNTAX"
  | "INVALID_TITLE"
  | "TOO_MANY_TOKENS";

export interface ParsedEntrySheetCommand {
  command: "credit" | "debit";
  account: string;
  accountNumber?: string;
  amount: number;
  letter?: string;
  comment?: string;
  raw: string;
}

export interface ParsedBeginningBalanceSheetCommand {
  command: "bbal";
  account: string;
  action: "set" | "delete";
  amount?: number;
  side?: "debit" | "credit";
  comment?: string;
  raw: string;
}

export interface ParsedClearSheetCommand {
  command: "clear";
  raw: string;
}

export interface ParsedCommentSheetCommand {
  command: "comment";
  letter: string;
  comment: string;
  raw: string;
}

export interface ParsedEditSheetCommand {
  command: "edit";
  reference: string;
  amount: number;
  side?: "debit" | "credit";
  comment?: string;
  raw: string;
}

export interface ParsedRenameSheetCommand {
  command: "rename";
  oldAccount: string;
  newAccount: string;
  raw: string;
}

export interface ParsedMoveSheetCommand {
  command: "move";
  reference: string;
  account: string;
  side?: "debit" | "credit";
  raw: string;
}

export interface ParsedTitleSheetCommand {
  command: "title";
  title: string;
  raw: string;
}

export interface ParsedDeleteSheetCommand {
  command: "delete";
  reference: string;
  raw: string;
}

export interface ParsedHelpSheetCommand {
  command: "help";
  raw: string;
}

export type ParsedSheetCommand =
  | ParsedEntrySheetCommand
  | ParsedBeginningBalanceSheetCommand
  | ParsedClearSheetCommand
  | ParsedCommentSheetCommand
  | ParsedEditSheetCommand
  | ParsedMoveSheetCommand
  | ParsedRenameSheetCommand
  | ParsedTitleSheetCommand
  | ParsedDeleteSheetCommand
  | ParsedHelpSheetCommand;

const COMMAND_ALIASES: Record<string, SheetCommandVerb> = {
  credit: "credit",
  cr: "credit",
  debit: "debit",
  db: "debit",
  bbal: "bbal",
  clear: "clear",
  comment: "comment",
  edit: "edit",
  move: "move",
  rename: "rename",
  title: "title",
  delete: "delete",
  help: "help",
};

export class ParseSheetCommandError extends Error {
  code: ParseSheetCommandErrorCode;

  constructor(code: ParseSheetCommandErrorCode, message: string) {
    super(message);
    this.name = "ParseSheetCommandError";
    this.code = code;
  }
}

export function parseSheetCommandBatch(input: string): ParsedSheetCommand[] {
  const commandInputs = splitCommandBatch(input);
  if (commandInputs.length === 1 && commandInputs[0].length === 0) {
    return [parseSheetCommand(input)];
  }

  if (commandInputs.some((commandInput) => commandInput.length === 0)) {
    throw new ParseSheetCommandError("EMPTY_COMMAND", "Commands separated by semicolons cannot be empty.");
  }

  const commands = commandInputs.map(parseSheetCommand);
  if (commands.length > 1 && commands.some((command) => command.command === "help")) {
    throw new ParseSheetCommandError("INVALID_COMMAND", "Command 'help' must be entered by itself.");
  }

  return commands;
}

function isAlphanumeric(value: string): boolean {
  return /^[a-z0-9]+$/i.test(value);
}

function isAlphanumericWithSpaces(value: string): boolean {
  return /^[a-z0-9 :]+$/i.test(value);
}

function isValidAccountNumber(value: string): boolean {
  return /^[a-z0-9.-]+$/i.test(value);
}

function isLineItemReference(value: string): boolean {
  return /^[a-z](?:\.\d+)?$/i.test(value);
}

function stripCurrencySymbol(value: string): string {
  return value.startsWith("$") ? value.slice(1) : value;
}

function normalizeAmount(value: string): string {
  return stripCurrencySymbol(value).replace(/,/g, "");
}

function isValidAmount(value: string): boolean {
  return /^(?:(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{1,2})?|\.\d{1,2})$/.test(stripCurrencySymbol(value));
}

function isValidSignedAmount(value: string): boolean {
  return /^-?(?:(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{1,2})?|\.\d{1,2})$/.test(stripCurrencySymbol(value));
}

function parseOptionalSide(value: string): "debit" | "credit" | null {
  const normalizedValue = value.toLowerCase();
  if (normalizedValue === "debit" || normalizedValue === "db") {
    return "debit";
  }

  if (normalizedValue === "credit" || normalizedValue === "cr") {
    return "credit";
  }

  return null;
}

function looksLikeAmount(value: string): boolean {
  return /^\$?-?(?:\d|\.)/.test(value);
}

function assertValidAccountName(rawAccount: string, isQuotedAccount: boolean, command: SheetCommandVerb): void {
  if (!isQuotedAccount && looksLikeAmount(rawAccount)) {
    throw new ParseSheetCommandError(
      "INVALID_ACCOUNT",
      `Expected account name after ${command}, but got '${rawAccount}'.`,
    );
  }

  if (isQuotedAccount ? !isAlphanumericWithSpaces(rawAccount) : !isAlphanumeric(rawAccount)) {
    throw new ParseSheetCommandError(
      "INVALID_ACCOUNT",
      "Account must be alphanumeric (letters and numbers), with spaces allowed when quoted.",
    );
  }

  if (rawAccount.length >= 100) {
    throw new ParseSheetCommandError("ACCOUNT_TOO_LONG", "Account must be less than 100 characters.");
  }
}

export function parseSheetCommand(input: string): ParsedSheetCommand {
  const text = String(input || "").trim();
  if (!text) {
    throw new ParseSheetCommandError("EMPTY_COMMAND", "Enter a command like 'credit cash 100'.");
  }

  const tokenized = tokenizeCommandInput(text);
  if (tokenized.hasUnterminatedQuote) {
    throw new ParseSheetCommandError("INVALID_COMMENT", "Quoted values must be enclosed in matching quotes.");
  }

  const tokens = tokenized.tokens;
  const parts = tokens.map((token) => token.value);
  const [rawCommand, rawAccount] = parts;
  const commandToken = rawCommand.toLowerCase();
  const command = COMMAND_ALIASES[commandToken];

  if (!command) {
    throw new ParseSheetCommandError("INVALID_COMMAND", "Command must start with credit/cr, debit/db, bbal, comment, edit, move, title, rename, delete, help, or clear.");
  }

  if (command === "clear") {
    if (parts.length > 1) {
      throw new ParseSheetCommandError("TOO_MANY_TOKENS", "Command 'clear' does not accept arguments.");
    }

    return {
      command: "clear",
      raw: text,
    };
  }

  if (command === "help") {
    if (parts.length > 1) {
      throw new ParseSheetCommandError("TOO_MANY_TOKENS", "Command 'help' does not accept arguments.");
    }

    return {
      command: "help",
      raw: text,
    };
  }

  if (command === "comment") {
    const [, rawLetter, rawCommentToken, extraToken] = tokens;

    if (!rawLetter) {
      throw new ParseSheetCommandError("INVALID_LETTER", "Comment command requires a single letter.");
    }

    if (!isLineItemReference(rawLetter.value)) {
      throw new ParseSheetCommandError("INVALID_LETTER", "Reference must be a line-item reference (e.g. a or a.1).");
    }

    if (!rawCommentToken) {
      throw new ParseSheetCommandError("MISSING_COMMENT", "Comment command requires a quoted comment.");
    }

    if (!rawCommentToken.quoteChar || !rawCommentToken.isClosedQuote) {
      throw new ParseSheetCommandError("INVALID_COMMENT", "Comment must be enclosed in quotes.");
    }

    if (extraToken) {
      throw new ParseSheetCommandError("TOO_MANY_TOKENS", "Too many values. Expected: comment <letter> \"text\".");
    }

    return {
      command: "comment",
      letter: rawLetter.value.toLowerCase(),
      comment: rawCommentToken.value,
      raw: text,
    };
  }

  if (command === "edit") {
    const [, rawReference, rawAmountToken, optionalSideToken, optionalCommentToken, extraToken] = tokens;

    if (!rawReference) {
      throw new ParseSheetCommandError("INVALID_LETTER", "Edit command requires a line-item reference (e.g. a or a.1).");
    }

    if (!isLineItemReference(rawReference.value)) {
      throw new ParseSheetCommandError("INVALID_LETTER", "Reference must be a line-item reference (e.g. a or a.1).");
    }

    if (!rawAmountToken) {
      throw new ParseSheetCommandError("MISSING_AMOUNT", `Expected amount after reference '${rawReference.value}'.`);
    }

    if (!isValidSignedAmount(rawAmountToken.value)) {
      throw new ParseSheetCommandError(
        "INVALID_AMOUNT",
        "Amount must be a valid currency value (e.g. 100, -100, or 150.25).",
      );
    }

    let side: "debit" | "credit" | undefined;
    let commentToken = optionalSideToken;
    if (optionalSideToken && !optionalSideToken.quoteChar) {
      const parsedSide = parseOptionalSide(optionalSideToken.value);
      if (parsedSide) {
        side = parsedSide;
        commentToken = optionalCommentToken;
      }
    }

    if (commentToken && (!commentToken.quoteChar || !commentToken.isClosedQuote)) {
      if (optionalSideToken && side === undefined && !optionalSideToken.quoteChar) {
        throw new ParseSheetCommandError("INVALID_OPTIONAL_TOKEN", "Optional side must be 'debit/db' or 'credit/cr'.");
      }

      throw new ParseSheetCommandError("INVALID_COMMENT", "Comment must be enclosed in quotes.");
    }

    if (extraToken) {
      throw new ParseSheetCommandError(
        "TOO_MANY_TOKENS",
        "Too many values. Expected: edit <line item ref> <amount> [debit|credit|db|cr] [\"comment\"].",
      );
    }

    return {
      command: "edit",
      reference: rawReference.value.toLowerCase(),
      amount: Number(normalizeAmount(rawAmountToken.value)),
      side,
      comment: commentToken?.value,
      raw: text,
    };
  }

  if (command === "move") {
    const [, rawReference, toKeywordToken, rawAccountToken, rawSideToken, extraToken] = tokens;

    if (!rawReference) {
      throw new ParseSheetCommandError("INVALID_LETTER", "Move command requires a line-item reference (e.g. a or a.1).");
    }

    if (!isLineItemReference(rawReference.value)) {
      throw new ParseSheetCommandError("INVALID_LETTER", "Reference must be a line-item reference (e.g. a or a.1).");
    }

    if (!toKeywordToken || toKeywordToken.value.toLowerCase() !== "to") {
      throw new ParseSheetCommandError(
        "INVALID_OPTIONAL_TOKEN",
        "Move syntax is: move <line item ref> to <account> [debit|credit].",
      );
    }

    if (!rawAccountToken) {
      throw new ParseSheetCommandError("MISSING_ACCOUNT", "Move command requires a target account after 'to'.");
    }

    assertValidAccountName(rawAccountToken.value, !!rawAccountToken.quoteChar, command);

    let side: "debit" | "credit" | undefined;
    if (rawSideToken) {
      const normalizedSide = rawSideToken.value.toLowerCase();
      if (normalizedSide !== "debit" && normalizedSide !== "credit") {
        throw new ParseSheetCommandError("INVALID_OPTIONAL_TOKEN", "Optional side must be 'debit' or 'credit'.");
      }

      side = normalizedSide;
    }

    if (extraToken) {
      throw new ParseSheetCommandError(
        "TOO_MANY_TOKENS",
        "Too many values. Expected: move <line item ref> to <account> [debit|credit].",
      );
    }

    return {
      command: "move",
      reference: rawReference.value.toLowerCase(),
      account: rawAccountToken.value,
      side,
      raw: text,
    };
  }

  if (command === "rename") {
    const [, oldAccountToken, toKeywordToken, newAccountToken, extraToken] = tokens;

    if (!oldAccountToken) {
      throw new ParseSheetCommandError("MISSING_ACCOUNT", "Rename command requires an existing account name.");
    }

    assertValidAccountName(oldAccountToken.value, !!oldAccountToken.quoteChar, command);

    if (!toKeywordToken || toKeywordToken.value.toLowerCase() !== "to") {
      throw new ParseSheetCommandError(
        "INVALID_RENAME_SYNTAX",
        "Rename syntax is: rename <old account name> to <new account name>.",
      );
    }

    if (!newAccountToken) {
      throw new ParseSheetCommandError("MISSING_ACCOUNT", "Rename command requires a new account name after 'to'.");
    }

    assertValidAccountName(newAccountToken.value, !!newAccountToken.quoteChar, command);

    if (extraToken) {
      throw new ParseSheetCommandError(
        "TOO_MANY_TOKENS",
        "Too many values. Expected: rename <old account name> to <new account name>.",
      );
    }

    return {
      command: "rename",
      oldAccount: oldAccountToken.value,
      newAccount: newAccountToken.value,
      raw: text,
    };
  }

  if (command === "title") {
    const [, rawTitleToken, extraToken] = tokens;

    if (!rawTitleToken) {
      throw new ParseSheetCommandError("INVALID_TITLE", "Title command requires a quoted title (e.g. title \"My document\").");
    }

    if (!rawTitleToken.quoteChar || !rawTitleToken.isClosedQuote) {
      throw new ParseSheetCommandError("INVALID_TITLE", "Title must be enclosed in quotes.");
    }

    if (extraToken) {
      throw new ParseSheetCommandError("TOO_MANY_TOKENS", "Too many values. Expected: title \"document title\".");
    }

    return {
      command: "title",
      title: rawTitleToken.value,
      raw: text,
    };
  }

  if (command === "delete") {
    const [, rawReference, extraToken] = tokens;

    if (!rawReference) {
      throw new ParseSheetCommandError("INVALID_LETTER", "Delete command requires a top-level reference (e.g. a).")
    }

    if (!/^[a-z]$/i.test(rawReference.value)) {
      throw new ParseSheetCommandError("INVALID_LETTER", "Delete reference must be a single top-level letter (e.g. a).")
    }

    if (extraToken) {
      throw new ParseSheetCommandError("TOO_MANY_TOKENS", "Too many values. Expected: delete <top-level reference>.");
    }

    return {
      command: "delete",
      reference: rawReference.value.toLowerCase(),
      raw: text,
    };
  }

  if (!rawAccount) {
    throw new ParseSheetCommandError("MISSING_ACCOUNT", `Expected account name after ${command}.`);
  }

  const accountToken = tokens[1];
  const isQuotedAccount = !!accountToken?.quoteChar;
  let parsedAccount = rawAccount;
  let parsedAccountNumber: string | undefined;
  let amountTokenIndex = 2;

  if ((command === "credit" || command === "debit") && accountToken) {
    if (isQuotedAccount) {
      const accountNumberToken = tokens[2];
      if (accountNumberToken?.value.startsWith(":")) {
        const suffix = accountNumberToken.value.slice(1);
        if (!suffix || !isValidAccountNumber(suffix)) {
          throw new ParseSheetCommandError(
            "INVALID_ACCOUNT",
            "Account number must be alphanumeric and may include '-' or '.'.",
          );
        }

        parsedAccountNumber = suffix;
        amountTokenIndex = 3;
      }
    } else {
      const separatorIndex = rawAccount.indexOf(":");
      if (separatorIndex >= 0) {
        const accountName = rawAccount.slice(0, separatorIndex);
        const accountNumber = rawAccount.slice(separatorIndex + 1);

        if (!accountName || !accountNumber || !isValidAccountNumber(accountNumber)) {
          throw new ParseSheetCommandError(
            "INVALID_ACCOUNT",
            "Account number must be alphanumeric and may include '-' or '.'.",
          );
        }

        parsedAccount = accountName;
        parsedAccountNumber = accountNumber;
      }
    }
  }

  assertValidAccountName(parsedAccount, isQuotedAccount, command);

  const resolvedAmountToken = tokens[amountTokenIndex]?.value;
  if (!resolvedAmountToken) {
    throw new ParseSheetCommandError("MISSING_AMOUNT", `Expected amount after account '${parsedAccount}'.`);
  }

  if (command === "bbal" && resolvedAmountToken.toLowerCase() === "delete") {
    if (tokens[amountTokenIndex + 1]) {
      throw new ParseSheetCommandError(
        "TOO_MANY_TOKENS",
        "Too many values. Expected: bbal <account> delete.",
      );
    }

    return {
      command: "bbal",
      account: parsedAccount,
      action: "delete",
      raw: text,
    };
  }

  const amountIsValid = command === "bbal" ? isValidSignedAmount(resolvedAmountToken) : isValidAmount(resolvedAmountToken);
  if (!amountIsValid) {
    throw new ParseSheetCommandError(
      "INVALID_AMOUNT",
      "Amount must be a valid currency value (e.g. 100 or 150.25).",
    );
  }

  if (command === "bbal") {
    const optionalSideToken = tokens[amountTokenIndex + 1];
    const optionalCommentToken = tokens[amountTokenIndex + 2];
    const extraToken = tokens[amountTokenIndex + 3];
    let side: "debit" | "credit" | undefined;
    let commentToken = optionalSideToken;

    if (optionalSideToken) {
      const parsedSide = !optionalSideToken.quoteChar
        ? parseOptionalSide(optionalSideToken.value)
        : null;
      if (parsedSide) {
        side = parsedSide;
        commentToken = optionalCommentToken;
      }
    }

    if (commentToken && (!commentToken.quoteChar || !commentToken.isClosedQuote)) {
      if (optionalSideToken && side === undefined && !optionalSideToken.quoteChar) {
        throw new ParseSheetCommandError("INVALID_OPTIONAL_TOKEN", "Optional side must be 'debit/db' or 'credit/cr'.");
      }

      throw new ParseSheetCommandError("INVALID_COMMENT", "Comment must be enclosed in quotes.");
    }

    if (extraToken) {
      throw new ParseSheetCommandError(
        "TOO_MANY_TOKENS",
        "Too many values. Expected: bbal <account> <amount> [debit|credit|db|cr] [\"comment\"].",
      );
    }

    return {
      command: "bbal",
      account: parsedAccount,
      action: "set",
      amount: Number(normalizeAmount(resolvedAmountToken)),
      side,
      comment: commentToken?.value,
      raw: text,
    };
  }

  const trailingTokens = tokens.slice(amountTokenIndex + 1);
  let comment: string | undefined;
  let optionalTokens = trailingTokens;

  const trailingCommentToken = trailingTokens[trailingTokens.length - 1];
  if (trailingCommentToken?.quoteChar && trailingCommentToken.isClosedQuote) {
    comment = trailingCommentToken.value;
    optionalTokens = trailingTokens.slice(0, -1);
  }

  if (optionalTokens.some((token) => !!token.quoteChar)) {
    throw new ParseSheetCommandError(
      "INVALID_COMMENT",
      "Comment must be the last argument and enclosed in double quotes.",
    );
  }

  if (optionalTokens.length > 2) {
    throw new ParseSheetCommandError(
      "TOO_MANY_TOKENS",
      "Too many values. Expected: command account amount [letter] [\"comment\"].",
    );
  }

  if (optionalTokens.length === 2) {
    throw new ParseSheetCommandError(
      "TOO_MANY_TOKENS",
      "Too many values. Expected: command account amount [letter] [\"comment\"].",
    );
  }

  let letter: string | undefined;
  if (optionalTokens.length === 1) {
    const rawOptionalToken = optionalTokens[0].value;
    if (!isLineItemReference(rawOptionalToken)) {
      throw new ParseSheetCommandError(
        "INVALID_OPTIONAL_TOKEN",
        "Fourth token must be a line-item reference (e.g. a or a.1).",
      );
    }

    letter = rawOptionalToken.toLowerCase();
  }

  const amount = Number(normalizeAmount(resolvedAmountToken));

  return {
    command,
    account: parsedAccount,
    accountNumber: parsedAccountNumber,
    amount,
    letter,
    comment,
    raw: text,
  };
}
